import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { firstAllowedPath } from '../utils/rbacDefaultRoute';
import { canAccessSecurityAdmin, hasFullRbacMenuBypass } from '../utils/rbacSecurityAccess';
import { requiredActionGrantForPath, requiredModuleKeyForPath } from '../utils/rbacRouteModule';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const path = location.pathname;
  if (path.startsWith('/security/') && !canAccessSecurityAdmin(user)) {
    return (
      <Navigate
        to={firstAllowedPath(user?.allowedModules, false, user?.allowedActions)}
        replace
      />
    );
  }

  if (hasFullRbacMenuBypass(user)) {
    return <>{children}</>;
  }

  const moduleKey = requiredModuleKeyForPath(path);
  const grant = requiredActionGrantForPath(path);

  if (moduleKey === null && grant === null) {
    return <>{children}</>;
  }

  const allowed = user?.allowedModules;
  if (allowed === undefined) {
    if (moduleKey === 'administracion') {
      return <Navigate to={firstAllowedPath(undefined, false, user?.allowedActions)} replace />;
    }
    return <>{children}</>;
  }

  /** Lista vacía: sin filas en users_role_permissions o fallo API; solo mapa (y rutas sin módulo, p. ej. perfil). */
  if (allowed.length === 0) {
    if (moduleKey === null || moduleKey === 'mapa') {
      return <>{children}</>;
    }
    return <Navigate to="/map" replace />;
  }

  if (moduleKey !== null && !allowed.includes(moduleKey)) {
    const to = firstAllowedPath(allowed, false, user?.allowedActions);
    if (to === path) {
      return <Navigate to="/map" replace />;
    }
    return <Navigate to={to} replace />;
  }

  /** Tokens antiguos sin `allowedActions`: se mantiene la comprobación solo por módulo. */
  const actions = user?.allowedActions;
  if (grant && Array.isArray(actions) && !actions.includes(grant)) {
    const to = firstAllowedPath(allowed, false, actions);
    if (to === path) {
      return <Navigate to="/map" replace />;
    }
    return <Navigate to={to} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
